import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { VehicleStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { selectVehicleDispositionByVehicleId } from 'src/store/entityStores/vehicle-disposition-selector';
import { VehicleDispositionEntityFetchService } from 'src/store/generated/client-sync.generated';
import {
  VehicleDispositionProps,
  VehicleDispositionViewModelBase,
} from 'src/store/generated/vehicle-disposition-record.generated';
import { VehicleRecord } from 'src/store/generated/vehicle-record.generated';
import { undefinedOrSwitchMap } from 'src/util/rxjs/operators';
import { humanize } from 'src/util/text';
import { getOrElse } from 'src/util/values';
import { QueryOptions } from 'src/store/fetch-services';
import {
  FlightFollowingReportViewModel,
  FlightFollowingReportViewModelService,
} from './flight-following-report-view-model';
import {
  PositionReportViewModel,
  PositionReportViewModelService,
} from './position-report-view-model';
import { WaypointViewModel, WaypointViewModelService } from './waypoint-view-model';

export const statusConfig: {
  [status in VehicleStatus]: {
    statusDisplay?: string;
  };
} = {
  AtWaypoint: {},
  EnRoute: {
    statusDisplay: 'En-Route',
  },
};

export function vehicleStatusDisplay(status: VehicleStatus): string {
  return getOrElse(statusConfig[status].statusDisplay, humanize(status));
}

export class VehicleDispositionViewModel extends VehicleDispositionViewModelBase {
  get hasTrip() {
    return this.currentTripId != null;
  }
  public waypoint$: Observable<WaypointViewModel | undefined>;
  public vehicle$: Observable<VehicleRecord>;

  get config() {
    return statusConfig[this.status];
  }

  get statusDisplay() {
    return vehicleStatusDisplay(this.status);
  }

  constructor(
    vehicleDisposition: VehicleDispositionProps,
    public readonly latestPositionReport: PositionReportViewModel | undefined,
    public readonly latestFlightFollowingReport: FlightFollowingReportViewModel | undefined,
    public readonly currentWaypoint: WaypointViewModel | undefined,
  ) {
    super(vehicleDisposition);
    // TODO: remove this.waypoint$
    this.waypoint$ = of(currentWaypoint);
  }
}
@Injectable({ providedIn: 'root' })
export class VehicleDispositionViewModelService {
  constructor(
    private readonly _vehicleDispositionEntityFetchService: VehicleDispositionEntityFetchService,
    private readonly _waypointVm: WaypointViewModelService,
    private readonly _positionReportVm: PositionReportViewModelService,
    private readonly _flightFollowingVm: FlightFollowingReportViewModelService,
    private _store: Store<AppState>,
  ) {}

  public getByVehicleId(vehicleId: string, options?: QueryOptions) {
    return this._store
      .select(selectVehicleDispositionByVehicleId(vehicleId))
      .pipe(undefinedOrSwitchMap((vd) => this._getVm(vd, options)));
  }

  public getById(
    id: string,
    options?: QueryOptions,
  ): Observable<VehicleDispositionViewModel | undefined> {
    return this._vehicleDispositionEntityFetchService
      .getById(id, options)
      .pipe(undefinedOrSwitchMap((vd) => this._getVm(vd, options)));
  }

  private _getVm(vd: VehicleDispositionProps, options?: QueryOptions) {
    const currentWaypoint$ =
      vd.currentWaypointId != null
        ? this._waypointVm.getById(vd.currentWaypointId, options)
        : of(undefined);
    const positionReport$ =
      vd.latestPositionReportId != null
        ? this._positionReportVm.getById(vd.latestPositionReportId, options)
        : of(undefined);
    const latestFlightFollowing$ =
      vd.latestFlightFollowingReportId != null
        ? this._flightFollowingVm.getById(vd.latestFlightFollowingReportId, options)
        : of(undefined);
    return combineLatest(
      currentWaypoint$,
      positionReport$.pipe(startWith(undefined)),
      latestFlightFollowing$,
    ).pipe(
      map(
        ([currentWaypoint, latestPositionReport, latestFlightFollowingReport]) =>
          new VehicleDispositionViewModel(
            vd,
            latestPositionReport,
            latestFlightFollowingReport,
            currentWaypoint,
          ),
      ),
    );
  }
}
